<template>
  <div class="upgrade-rules-management-page">
    <hr class="separator" style="margin-top: 1px"/>

    <div>
      <div class="custom-text">
        <span class="user-manage">{{ $t('SpecialAccountManagement.title') }}</span>
      </div>
      <hr class="separator"/>
    </div>
    <div class="content-container">
      <div class="search-area">
        <!-- 搜索功能组件 -->
        <SearchBar @search="handleSearch"/>
      </div>
      
      <!-- 新增账号按钮 -->
      <div style="text-align: right; margin-bottom: 16px;">
        <el-button class="button-background-color" @click="handleAdd">
          <el-icon><Plus/></el-icon>&nbsp;{{ $t('SpecialAccountManagement.addSpecialAccount') }}
        </el-button>
      </div>

      <!-- 特殊账号记录表格 -->
      <DataTable :data="tableData" :loading="loading" @refresh="loadData" @edit="handleEdit" @enable="handleEnable"/>

      <!-- 分页组件 -->
      <div class="pagination">
        <PaginationBar :totalRecords="totalRecords" :current-page="currentPage" @page-change="handlePageChange"
                       @page-size-change="handlePageSizeChange" :page-size="pageSize"/>
      </div>
    </div>

    <!-- 添加/编辑对话框 -->
    <el-dialog :model-value="dialogVisible" :title="getDialogTitle()" width="500px" :show-close="true" @close="closeDialog" @update:model-value="dialogVisible = $event">
      <el-form :model="formData" :rules="formRules" ref="formRef" label-width="120px">
        <el-form-item :label="$t('SpecialAccountManagement.userAccount')" prop="userAccount">
          <el-select 
            v-model="formData.userAccount" 
            style="width: 100%"
            :loading="usersLoading" 
            filterable 
            clearable 
            remote 
            :remote-method="loadUsersList"
            :placeholder="$t('SpecialAccountManagement.userAccountPlaceholder')"
          >
            <el-option 
              v-for="user in usersList" 
              :key="user.id" 
              :label="user.email || user.name || user.id" 
              :value="user.id" 
            />
          </el-select>
        </el-form-item>
        <el-form-item :label="$t('SpecialAccountManagement.beginAt')" prop="beginAt">
          <el-date-picker
            v-model="formData.beginAt"
            type="datetime"
            style="width: 100%"
            :placeholder="$t('SpecialAccountManagement.beginAtPlaceholder')"
            value-format="YYYY-MM-DD HH:mm:ss"
            popper-class="date-picker-popper"
          />
        </el-form-item>
        <el-form-item :label="$t('SpecialAccountManagement.endAt')" prop="endAt">
          <el-date-picker
            v-model="formData.endAt"
            type="datetime"
            style="width: 100%"
            :placeholder="$t('SpecialAccountManagement.endAtPlaceholder')"
            value-format="YYYY-MM-DD HH:mm:ss"
            popper-class="date-picker-popper"
          />
        </el-form-item>
        <el-form-item :label="$t('SpecialAccountManagement.enable')" prop="enable">
          <el-switch v-model="formData.enable"/>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="closeDialog">{{ $t('SpecialAccountManagement.cancel') }}</el-button>
        <el-button @click="submitForm" :loading="submitLoading" type="primary">{{ $t('SpecialAccountManagement.save') }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import SearchBar from './components/SearchBar.vue';
import DataTable from './components/DataTable.vue';
import PaginationBar from '@/components/PaginationBar.vue';
import { Plus } from '@element-plus/icons-vue';
import { getSpecialAccounts, addSpecialAccount, updateSpecialAccount, getUserPermissions, getUsersSimple } from '@/api/api';
import { ElMessage } from 'element-plus';

export default {
  components: {
    SearchBar,
    DataTable,
    PaginationBar,
    Plus
  },
  data() {
    return {
      tableData: [], // 特殊账号数据
      totalRecords: 0, // 总记录数
      currentPage: 1, // 当前页码
      pageSize: 10,
      isSearching: false,
      searchParams: {},
      loading: false, // 加载状态
      dialogVisible: false,
      submitLoading: false,
      selectedId: null,
      isEdit: false,
      formData: {
        userAccount: '',
        beginAt: '',
        endAt: '',
        enable: true
      },
      formRules: {
        userAccount: [
          { required: true, message: this.$t('SpecialAccountManagement.userAccountRequired'), trigger: 'blur' }
        ],
        beginAt: [
          { required: true, message: this.$t('SpecialAccountManagement.beginAtRequired'), trigger: 'blur' }
        ],
        endAt: [
          { required: true, message: this.$t('SpecialAccountManagement.endAtRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value && this.formData.beginAt && new Date(value) <= new Date(this.formData.beginAt)) {
                callback(new Error(this.$t('SpecialAccountManagement.endAtInvalid')));
              } else {
                callback();
              }
            },
            trigger: 'blur'
          }
        ]
      },
      userPermissions: [],
      usersList: [], // 账号列表数据
      usersLoading: false // 账号数据加载状态
    };
  },

  created() {
    this.loadData();
  },

  mounted() {
    this.loadUserPermissions();
  },

  watch: {
    // 监听语言切换事件
    '$i18n.locale': function() {
      // 语言切换后重新加载表格数据
      if (this.isSearching) {
        this.handleSearch(this.searchParams);
      } else {
        this.loadData();
      }
    }
  },

  methods: {
    // 加载用户权限
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        
        // 首先尝试从localStorage获取
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        } else {
          // 如果本地没有缓存，则调用API获取权限
          const rawPermissions = await getUserPermissions();
          localStorage.setItem(cacheKey, JSON.stringify(rawPermissions.menus));
          this.userPermissions = rawPermissions.menus;
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },

    // 检查权限
    hasPermission(permission) {
      if (!this.userPermissions || this.userPermissions.length === 0) {
        return false;
      }
      return this.userPermissions.some(item => item.name === permission);
    },

    // 搜索处理
    async handleSearch(params) {
      console.log('搜索参数:', params);
      this.isSearching = true;
      this.searchParams = params;
      this.currentPage = 1;
      this.loading = true;
      try {
        // 直接使用从SearchBar组件传递过来的格式化搜索参数
        console.log('构建的搜索参数:', params);
        const data = await getSpecialAccounts(this.currentPage, this.pageSize, params);
        console.log('搜索结果:', data);
        this.tableData = data.data.map(item => ({
          id: item.id,
          userId: item.user_id,
          beginAt: item.begin_at,
          endAt: item.end_at,
          createdBy: item.created_by,
          enable: item.enable,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          user: item.user, // 添加用户信息，用于显示用户邮箱
          createdUser: item.createdUser // 添加创建人信息，用于显示创建人邮箱
        }));
        this.totalRecords = data.meta.total;
        this.currentPage = data.meta.current_page;
        this.pageSize = typeof data.meta.per_page === 'string' ? parseInt(data.meta.per_page, 10) : data.meta.per_page;
      } catch (error) {
        console.error('搜索失败:', error);
        ElMessage.error(this.$t('SpecialAccountManagement.searchFailed'));
      } finally {
        this.loading = false;
      }
    },

    // 编辑处理
    handleEdit(row) {
      this.isEdit = true;
      this.selectedId = row.id;
      this.formData = {
        userAccount: row.userId, // 修改这里，使用userAccount而不是userId
        beginAt: row.beginAt,
        endAt: row.endAt,
        enable: !!row.enable // 确保是布尔值
      };
      this.dialogVisible = true;
      // 加载用户列表以确保选项正确显示
      this.loadUsersList();
    },

    // 启用/禁用处理
    async handleEnable(id, enable) {
      try {
        const currentItem = this.tableData.find(item => item.id === id);
        await updateSpecialAccount(id, {
          userId: currentItem.userId,
          beginAt: currentItem.beginAt,
          endAt: currentItem.endAt,
          enable: enable
        });
        ElMessage.success(this.$t('SpecialAccountManagement.toggleSuccess'));
        this.loadData();
      } catch (error) {
        console.error(error);
        ElMessage.error(this.$t('SpecialAccountManagement.updateFailed'));
      }
    },

    // 加载数据
    async loadData() {
      this.isSearching = false;
      this.loading = true;
      try {
        const data = await getSpecialAccounts(this.currentPage, this.pageSize);
        this.tableData = data.data.map(item => ({
          id: item.id,
          userId: item.user_id,
          beginAt: item.begin_at,
          endAt: item.end_at,
          createdBy: item.created_by,
          enable: item.enable,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          user: item.user, // 添加用户信息，用于显示用户邮箱
          createdUser: item.createdUser // 添加创建人信息，用于显示创建人邮箱
        }));
        this.totalRecords = data.meta.total;
        this.currentPage = data.meta.current_page;
        this.pageSize = typeof data.meta.per_page === 'string' ? parseInt(data.meta.per_page, 10) : data.meta.per_page;
      } catch (error) {
        console.error(error);
        ElMessage.error(this.$t('SpecialAccountManagement.loadDataFailed'));
      } finally {
        this.loading = false;
      }
    },

    // 页码变化处理
    async handlePageChange(newPage) {
      this.currentPage = newPage;
      try {
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(error);
        ElMessage.error(this.$t('SpecialAccountManagement.loadDataFailed'));
      }
    },

    // 每页记录数变化处理
    async handlePageSizeChange(newPageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      try {
        if (this.isSearching) {
          await this.handleSearch(this.searchParams);
        } else {
          await this.loadData();
        }
      } catch (error) {
        console.error(error);
        ElMessage.error(this.$t('SpecialAccountManagement.loadDataFailed'));
      }
    },

    // 处理新增按钮点击
    handleAdd() {
      this.isEdit = false;
      this.formData = {
        userAccount: '',
        beginAt: '',
        endAt: '',
        enable: true
      };
      this.dialogVisible = true;
      // 加载用户列表
      this.loadUsersList();
    },
    
    // 加载用户列表数据，支持搜索
    async loadUsersList(query = '') {
      try {
        this.usersLoading = true;
        const res = await getUsersSimple();
        
        if (res && res.code === 0 && Array.isArray(res.data)) {
          if (query) {
            // 如果有搜索词，在本地过滤数据
            this.usersList = res.data.filter(user => 
              // 在email、name或id中搜索
              (user.email && user.email.toLowerCase().includes(query.toLowerCase())) ||
              (user.name && user.name.toLowerCase().includes(query.toLowerCase())) ||
              (user.id && user.id.toString().includes(query))
            );
          } else {
            // 如果没有搜索词，显示全部数据
            this.usersList = res.data;
          }
        } else {
          ElMessage.error(this.$t('SpecialAccountManagement.loadUsersFailed'));
          this.usersList = [];
        }
      } catch (error) {
        console.error('加载用户列表失败:', error);
        ElMessage.error(this.$t('SpecialAccountManagement.loadUsersFailed'));
        this.usersList = [];
      } finally {
        this.usersLoading = false;
      }
    },
    
    // 获取对话框标题
    getDialogTitle() {
      return this.isEdit ? this.$t('SpecialAccountManagement.edit') : this.$t('SpecialAccountManagement.add');
    },

    // 提交表单
    async submitForm() {
      const formRef = this.$refs.formRef;
      if (!formRef) return;
      
      try {
        await formRef.validate();
        this.submitLoading = true;
        
        const formDataToSubmit = {
          user_id: parseInt(this.formData.userAccount),
          begin_at: this.formData.beginAt,
          end_at: this.formData.endAt,
          enable: this.formData.enable
        };
        
        if (this.isEdit) {
          await updateSpecialAccount(this.selectedId, formDataToSubmit);
          ElMessage.success(this.$t('SpecialAccountManagement.updateSuccess'));
        } else {
          await addSpecialAccount(formDataToSubmit);
          ElMessage.success(this.$t('SpecialAccountManagement.addSuccess'));
        }
        
        this.closeDialog();
        this.loadData();
      } catch (error) {
        console.error(error);
        ElMessage.error(this.$t('SpecialAccountManagement.submitFailed'));
      } finally {
        this.submitLoading = false;
      }
    },

    // 关闭对话框
    closeDialog() {
      this.dialogVisible = false;
      const formRef = this.$refs.formRef;
      if (formRef) {
        formRef.resetFields();
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/publicStyle.css";

.upgrade-rules-management-page {
  width: 100%;
  min-height: 0;
  height: calc(100vh - 48px);
  display: flex;
  flex-direction: column;
}

.content-container {
  width: 100%;
  flex: 1;
  min-height: 0;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  overflow: hidden;
}

/* 搜索区域样式 */
.search-area {
  margin-bottom: 16px;
}

/* 分页组件样式 */
.pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
  margin-bottom: 20px;
}

/* 修复日期选择器弹出位置 */
:deep(.date-picker-popper) {
  left: 0 !important;
  right: auto !important;
  transform: none !important;
}

/* 确保用户账号选择框样式正确 */
:deep(.el-select) {
  width: 100%;
}
</style>